import React, { useEffect, useRef, useState } from "react";
import Dropdown from "../Common/Dropdown/main";
import {
  FaCalendarAlt,
  FaMoneyBill,
  FaSpinner,
  FaUser,
} from "react-icons/fa";
import { BsChatDotsFill } from "react-icons/bs";
import { useProperties } from "../../hook/useProperties";
import { useTenants } from "../../hook/useTenents";

import { useUsers } from "../../hook/useUsers";
import SuggestionInput from "../Common/SuggestionInput/main";


interface ExpenseInfo {
  propertyName: string;
  expenseId: string;
  tenantName: string;
  expenseType: string;
  amount: number;
  date: string;
  comments: string;
}

interface ExpenseFormProps {
  initialData: ExpenseInfo;
  onSubmit: (formData: ExpenseInfo) => void;
  onCancel?: () => void;
  isOpen: boolean;
  id?: string;
  isLoading?: boolean;
  setActiveAddressId: (value: any) => void;
  activeAddressId?: number;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isOpen,
  id,
  isLoading,
  setActiveAddressId,
  activeAddressId, 
}) => {
  const { Property, queryClient } = useProperties();
  const { _Tenants } = useTenants();
  const { GetUserById } = useUsers();
  const uid = localStorage.getItem("R_M_uid") as string;
  const [formData, setFormData] = useState<ExpenseInfo>(initialData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const PropertyData = Property({ active: true, delete: false });
  const TenantsData = _Tenants();
  const UsersData = GetUserById(uid);

  // Create a ref for the entire form to handle outside clicks
  const formRef = useRef<HTMLDivElement>(null);
  const suggestionInputRef = useRef<HTMLDivElement>(null);

  const [expenseTypeOpen, setExpenseTypeOpen] = useState(false);
  const [propertyInputOpen, setPropertyInputOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close expense type dropdown if clicked outside
      if (expenseTypeOpen && formRef.current && 
          !formRef.current.contains(event.target as Node)) {
        setExpenseTypeOpen(false);
      }
      
      // Close property input if clicked outside
      // if (propertyInputOpen && suggestionInputRef.current && 
      //     !suggestionInputRef.current.contains(event.target as Node)) {
      //   setPropertyInputOpen(false);
      // }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expenseTypeOpen, propertyInputOpen]);

  useEffect(() => {
    setFormData({
      ...initialData,
    });
    
    // If we have an activeAddressId prop, ensure it's set
    // if (activeAddressId) {
    //   setActiveAddressId(activeAddressId);
    // }
  }, [initialData]); 

  const handleExpenseTypeClick = () => {
    setExpenseTypeOpen(!expenseTypeOpen);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  // Fixed setAddress function to handle both occupied and unoccupied properties
  const setAddress = (value: number) => {
    const allProperties = [
      ...(PropertyData as any)?.data?.data?.COMMERCIAL ?? [],
      ...(PropertyData as any)?.data?.data?.RESIDENTIAL ?? [],
    ];
    
    const selectedProperty = allProperties[value];
    const selectedPropertyName = selectedProperty?.propertyName ?? "";
    const selectedPropertyId = selectedProperty?.id;
    
    // Get tenant name if property is occupied, otherwise leave empty
    const tenantName = selectedProperty?.propertyStatus === "OCCUPIED" 
      ? selectedProperty?.tenantName ?? "" 
      : "";

    setFormData({
      ...formData,
      propertyName: selectedPropertyName,
      tenantName: tenantName,
    });
    setActiveAddressId(selectedPropertyId);
   // setPropertyInputOpen(false); // Close the property input after selection
  };

  // Handle direct property selection from SuggestionInput
  const handlePropertySelect = (propertyName: string, index: number) => {
    setFormData({ 
      ...formData, 
      propertyName: propertyName 
    });
    
    // Find the actual property index in the full list (not filtered)
    const allProperties = [
      ...(PropertyData?.data?.data?.COMMERCIAL ?? []),
      ...(PropertyData?.data?.data?.RESIDENTIAL ?? []),
    ];
    
    const actualIndex = allProperties.findIndex(
      prop => prop.propertyName === propertyName
    );
    
    if (actualIndex !== -1) {
      setAddress(actualIndex);
    }
    setPropertyInputOpen(false); // Close the property input after selection
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const fields = [
      "propertyName",
      "expenseType",
      "amount",
      "date",
    ];

    fields.forEach((field) => {
      if (!formData[field as keyof ExpenseInfo]) {
        newErrors[field] = `${field.replace(/([A-Z])/g, " $1")} is required.`;
      }
    });

    if (isNaN(Number(formData.amount))) {
      newErrors.amount = "Amount must be a valid number.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div ref={formRef} className="bg-white rounded-lg w-full max-w-3xl h-full mx-auto p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-lg font-bold">
          Expense Information{" "}
          <span className="text-md italic pr-4">{id ? id : ""}</span>
        </h1>
      </div>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2">
          <div className="">
            <label
              htmlFor="propertyName"
              className="text-sm text-gray-700 font-bold flex items-center"
            >
              <FaUser className="mr-2 h-4 w-4 text-blue-500" />
              Property Name
            </label>

              <SuggestionInput
                setActiveValue={(value: string) => {
                  setFormData({ ...formData, propertyName: value });
                }}
                activeValue={formData?.propertyName}
                loading={PropertyData.isLoading}
                setActiveIndex={(index: number) => {
                  // Get filtered properties (for suggestions)
                  const filteredProperties = [
                    ...(PropertyData?.data?.data?.COMMERCIAL ?? []),
                    ...(PropertyData?.data?.data?.RESIDENTIAL ?? []),
                  ];

                  const selectedPropertyNameFromSuggestion = filteredProperties[index]?.propertyName;

                  const allProperties = [
                    ...(PropertyData?.data?.data?.COMMERCIAL ?? []),
                    ...(PropertyData?.data?.data?.RESIDENTIAL ?? []),
                  ];

                  const actualSelectedIndex = allProperties.findIndex(
                    (prop) => prop.propertyName === selectedPropertyNameFromSuggestion
                  );

                  if (actualSelectedIndex !== -1) {
                    setAddress(actualSelectedIndex);
                  } else {
     // console.warn("Could not find the selected property in the full list.");
    }
                }}
                suggestions={
                  PropertyData.data == undefined
                    ? []
                    : [
                        ...(PropertyData?.data?.data?.COMMERCIAL ?? []),
                        ...(PropertyData?.data?.data?.RESIDENTIAL ?? []),
                      ].map((Item) => Item.propertyName)
                }
              />
            {/* </div> */}
            {errors?.propertyName && (
              <p className="text-red-500 text-xs italic">
                {errors?.propertyName}
              </p>
            )}
          </div>
          <div className="">
            <label
              htmlFor="tenantName"
              className="text-sm text-gray-700 font-bold flex items-center"
            >
              <FaUser className="mr-2 h-4 w-4 text-blue-500" />
              Tenant Name
            </label>
            <input
              disabled={true}
              type="text"
              id="tenantName"
              value={formData?.tenantName || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder={formData.tenantName ? "" : "No tenant assigned"}
            />
            {errors?.tenantName && (
              <p className="text-red-500 text-xs italic">
                {errors?.tenantName}
              </p>
            )}
          </div>
          <div className="">
            <label
              htmlFor="expenseType"
              className="text-sm text-gray-700 font-bold flex items-center"
            >
              <FaMoneyBill className="mr-2 h-4 w-4 text-yellow-500" />
              Expense Type
            </label>
            <div onClick={handleExpenseTypeClick}>
              <Dropdown
                className="w-[100%]"
                options={[
                  "UTILITIES",
                  "PROPERTY_MANAGEMENT_FEES",
                  "REPAIRS_AND_MAINTENANCE",
                  "GARDEN_AND_LAWN_CARE",
                  "CLEANING",
                  "PEST_CONTROL",
                  "PROPERTY_TAXES",
                  "SOCIETY_ASSOCIATION_FEES",
                  "SECURITY_AND_MONITORING",
                  "LEASING_COSTS",
                  "LEGAL_AND_PROFESSIONAL_FEES"
                ]}
                className2="py-2"
                onSelect={(e: any) => {
                  setFormData({ ...formData, expenseType: e });
                  setExpenseTypeOpen(false);
                }}
                activeOption={formData?.expenseType}
                width={305}
                isOpen={expenseTypeOpen}
                setIsOpen={setExpenseTypeOpen}
                closeOtherDropdown={() => {}}
              />
            </div>
            {errors?.expenseType && (
              <p className="text-red-500 text-xs italic">
                {errors?.expenseType}
              </p>
            )}
          </div>
          <div className="">
            <label
              htmlFor="amount"
              className="text-sm text-gray-700 font-bold flex items-center"
            >
              <FaMoneyBill className="mr-2 h-4 w-4 text-yellow-500" />
             Expense Amount
            </label>
            <input
              type="number"
              id="amount"
              value={formData?.amount}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.amount && (
              <p className="text-red-500 text-xs italic">
                {errors?.amount}
              </p>
            )}
          </div>
          <div className="">
            <label
              htmlFor="date"
              className="text-sm text-gray-700 font-bold flex items-center"
            >
              <FaCalendarAlt className="mr-2 h-4 w-4 text-green-500" />
              Date
            </label>
            <input
              type="date"
              id="date"
              value={formData?.date || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.date && (
              <p className="text-red-500 text-xs italic">{errors?.date}</p>
            )}
          </div>
        </div>
        <div className="">
          <label
            htmlFor="comments"
            className="text-sm text-gray-700 font-bold flex items-center"
          >
            <BsChatDotsFill className="mr-2 h-4 w-4 text-green-500" />
            Comments
          </label>
          <textarea
            id="comments"
            value={formData?.comments || ""}
            onChange={handleChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="flex flex-col sm:flex-row justify-end mt-4 space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            disabled={isLoading}
            type="submit"
            className="bg-primary hover:bg-secondary text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {isLoading ? (
              <p className="flex justify-center items-center">
                <FaSpinner className="animate-spin mr-2" /> Updating...
              </p>
            ) : (
              "Save"
            )
            }
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-red-400 hover:bg-red-500 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseForm;